import merge from 'lodash/merge';
import once from 'lodash/once';

import { HttpError, SentryIgnoreError } from '../model/errors';
import { clearCookie, getCookieAsInteger, REDIRECT_COUNT, setCookie } from '../utils/cookie';
import { DirectoryErrorCode, type RestJsonErrorResponse } from '../utils/error';
import { withDefaultExponentialBackoff } from '../utils/http';
import { handleResponse } from '../utils/status-code-handlers-provider';

interface FetchOptions {
	headers?: {
		[key: string]: string;
	};
	ignoreResponse?: boolean;
	body?: any; // tslint:disable-line no-any
	method?: string;
}

const defaultFetchOptions = {
	credentials: 'include',
	headers: {
		Accept: 'application/json',
	},
	mode: 'cors',
};

const fetchWithExponentialBackoff = withDefaultExponentialBackoff(fetch);

// tslint:disable-next-line no-any
function parseResponse(response: Response): Promise<any> | null {
	const responseStatusCode = response.status;
	if (responseStatusCode === 204 || responseStatusCode === 202) {
		return null;
	}

	const contentType = response.headers.get('content-type');
	if (!contentType || contentType.includes('text/html') || contentType.includes('text/plain')) {
		return response.text();
	}

	if (contentType.includes('application/json')) {
		return response.json();
	}

	return null;
}

export async function parseErrorMessage(response: Response): Promise<string> {
	let json: RestJsonErrorResponse;
	const text = await response.text();

	try {
		json = JSON.parse(text);
	} catch (err) {
		return text;
	}

	delete json.timestamp; // to dedupe sentry, scrub non-unique 'timestamp' field from the error body

	if (json.errors) {
		const errs: string[] = json.errors.map((error): string =>
			typeof error === 'string'
				? error
				: error.code || error.message || DirectoryErrorCode.MISSING_ERROR_MESSAGE,
		);

		return errs.join('\n - ');
	}

	return JSON.stringify(json);
}

let incrementRedirectCount: () => void;
let clearRedirectCount: () => void;
resetRedirectCountOnceFlag();

// Used by test to reset the once-ness of the functions above
export function resetRedirectCountOnceFlag(): void {
	incrementRedirectCount = once(() =>
		setCookie(REDIRECT_COUNT, getCookieAsInteger(REDIRECT_COUNT) + 1),
	);

	clearRedirectCount = once(() => clearCookie(REDIRECT_COUNT));
}

class RestClient {
	constructor({
		serviceUrl = '',
	}: {
		serviceUrl?: string;
	} = {}) {
		this.serviceUrl = serviceUrl;
	}

	serviceUrl: string;

	async getResource<T>(path: string): Promise<T> {
		return this.makeRequest(path);
	}

	async postResource<T>(
		path: string,
		data?: Record<string, unknown>,
		fetchOptions: FetchOptions = {},
	): Promise<T> {
		return this.postResourceRaw(path, JSON.stringify(data), fetchOptions);
	}

	async postResourceRaw<T>(
		path: string,
		data?: string,
		fetchOptions: FetchOptions = {},
	): Promise<T> {
		fetchOptions = {
			method: 'post',
			headers: {},
			...fetchOptions,
		};

		if (data) {
			fetchOptions = {
				body: data,
				...fetchOptions,
				headers: {
					'Content-Type': 'application/json',
					...fetchOptions.headers,
				},
			};
		}

		return this.makeRequest(path, fetchOptions);
	}

	async patchResource<T>(path: string, data?: Record<string, unknown>): Promise<T> {
		let fetchOptions: FetchOptions = {
			/**
			 * PATCH must be uppercase as per fetch spec only
			 * DELETE, GET, HEAD, OPTIONS, POST, and PUT methods
			 * are normalised to uppercase.
			 *
			 * https://github.com/github/fetch/issues/37
			 * https://fetch.spec.whatwg.org/#methods
			 */
			method: 'PATCH',
		};

		if (data) {
			fetchOptions = {
				...fetchOptions,
				body: JSON.stringify(data),
				headers: {
					'Content-Type': 'application/json',
				},
			};
		}

		return this.makeRequest(path, fetchOptions);
	}

	async putResource<T>(
		path: string,
		data?: Record<string, unknown>,
		additionalFetchOptions: FetchOptions = {},
	): Promise<T> {
		const fetchOptions = {
			method: 'put',
		};

		if (data) {
			Object.assign(fetchOptions, {
				body: JSON.stringify(data),
				headers: {
					'Content-Type': 'application/json',
				},
			});
		}

		return this.makeRequest(path, {
			...fetchOptions,
			...additionalFetchOptions,
		});
	}

	async deleteResource<T>(path: string, options: FetchOptions = {}): Promise<T> {
		const fetchOptions = {
			...options,
			method: 'delete',
		};

		return this.makeRequest(path, fetchOptions);
	}

	/**
	 * This function is necessary for if `statusCodeHandler` prescribes a login
	 * redirect upon a 401 response.
	 * While the default redirect behaviour may be overriden, this function has
	 * been kept separate to ensure it cannot be overriden, lest a host
	 * application supply embeddable-directory with a custom handler that suffers
	 * the infinite redirect bug.
	 */
	preventInfiniteRedirectLoop = (response: Response): void => {
		if (!response.ok) {
			const responseStatusCode = response.status;
			if (responseStatusCode === 401) {
				incrementRedirectCount();
			}
		} else {
			clearRedirectCount();
		}
	};

	async makeRequest<T>(path: string, additionalFetchOptions: FetchOptions = {}): Promise<T> {
		const url = this.serviceUrl + path;
		const options = merge({}, defaultFetchOptions, additionalFetchOptions);
		const response = await fetchWithExponentialBackoff(url, options);

		this.preventInfiniteRedirectLoop(response);

		handleResponse(response);

		const responseStatusCode = response.status;
		const traceId = response.headers.get('atl-traceid');
		if (!response.ok) {
			if (responseStatusCode === 401) {
				throw new SentryIgnoreError({
					message: 'Unauthorized Request - incrementing redirect count',
				});
			} else {
				const message = await parseErrorMessage(response);
				throw new HttpError({
					message,
					status: responseStatusCode,
					traceId: traceId ?? undefined,
				});
			}
		}

		// @ts-ignore - TS2322 TypeScript 5.9.2 upgrade
		return additionalFetchOptions.ignoreResponse ? null : parseResponse(response);
	}
}

export default RestClient;
